import React, { useState, useEffect } from 'react';
import { Star, PencilLine } from 'lucide-react';
import { reviewsAPI } from '../../services/api';

/**
 * Amazon-style rating badge for the academy header, next to Share: a row of
 * 5 stars filled to the rounded average, followed by the review COUNT as a
 * blue link (not the average number itself — that's what the star fill
 * communicates). Fetches its own summary (a single cheap aggregated call,
 * not the full review list) so it works independent of whether OverviewTab
 * is mounted.
 *
 * WITH NO REVIEWS IT INVITES ONE. This used to render nothing at all until an
 * academy had its first review — which meant a brand new academy showed no way
 * to review it anywhere in the header, and the one thing that would have
 * produced a first review was hidden until somebody had already left one.
 */
const AcademyRatingBadge = ({ academyId, onClick, onWrite }) => {
  const [summary, setSummary] = useState({ averageRating: null, totalReviews: 0 });

  useEffect(() => {
    if (!academyId) return;
    let cancelled = false;
    reviewsAPI.listForTarget('academy', academyId, { limit: 1 })
      .then(res => { if (!cancelled) setSummary(res.summary || { averageRating: null, totalReviews: 0 }); })
      .catch(err => console.error('Failed to load rating summary:', err));
    return () => { cancelled = true; };
  }, [academyId]);

  // No reviews yet: offer to write the first one rather than showing nothing.
  if (summary.averageRating == null) {
    return (
      <button
        onClick={onWrite}
        className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg hover:bg-gray-100 transition-colors"
        title="Be the first to review this academy"
      >
        <PencilLine size={15} className="text-blue-600" />
        <span className="text-sm text-blue-600 hover:underline">Write a Review</span>
      </button>
    );
  }

  const filledStars = Math.round(summary.averageRating);

  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg hover:bg-gray-100 transition-colors"
      title={`${summary.averageRating} out of 5 stars`}
    >
      <div className="flex">
        {[1, 2, 3, 4, 5].map(n => (
          <Star
            key={n}
            size={15}
            className={n <= filledStars ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300 fill-gray-300'}
          />
        ))}
      </div>
      <span className="text-sm text-blue-600 hover:underline">{summary.totalReviews}</span>
    </button>
  );
};

export default AcademyRatingBadge;
